import {
  ConditionNode,
  GetEvaluationExamplesResponse,
  GetHighlightsResponse,
  GuardrailEditDataByDocumentResponse,
  ReviewStatus,
  RunEvaluationRequest,
  RunEvaluationResponse,
} from "@financial-advisory/data-access/api-types";
import { ProcessedClause } from "@financial-advisory/data-access/api-types";
import { IHighlight } from "@financial-advisory/ui/components/pdf-viewer";
import { ingestionApi } from "./ingestionApi";

export const ProcessedClauseApi = ingestionApi.injectEndpoints({
  endpoints: (builder) => ({
    getProcessedClauses: builder.query<
      { data: ProcessedClause[] },
      { regulationId: string } | void
    >({
      query: (args) => ({
        url: args?.regulationId
          ? `regulations/${args.regulationId}/processed-clauses`
          : `processed-clauses`,
        method: "GET",
      }),
      transformResponse: (response: {
        data: Record<string, unknown>[];
      }): { data: ProcessedClause[] } => ({
        data: response.data.map((item) => {
          // Nested shape (uncheck): { recordId, reviewStatus, data: { id, ... } }
          if ("data" in item && "recordId" in item) {
            const clause = item.data as Record<string, unknown>;
            return {
              ...clause,
              recordId: item.recordId,
              reviewStatus: item.reviewStatus,
            } as ProcessedClause;
          }
          // Flat shape (ingestion service): { id, title, ... }
          return item as ProcessedClause;
        }),
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.data.map(({ id }) => ({
              type: "ProcessedClause" as const,
              id,
            })),
            { type: "ProcessedClause", id: "LIST" },
          ]
          : [{ type: "ProcessedClause", id: "LIST" }],
    }),
    reviewProcessedClause: builder.mutation<
      void,
      {
        regulationId: string;
        processedClauseId: string;
        reviewStatus: ReviewStatus;
      }
    >({
      query: ({ regulationId, processedClauseId, reviewStatus }) => ({
        url: `regulations/${regulationId}/processed-clauses/${processedClauseId}/review`,
        method: "POST",
        body: { review_status: reviewStatus },
      }),
      invalidatesTags: [{ type: "ProcessedClause", id: "LIST" }],
    }),
    getHighlights: builder.query<
      GetHighlightsResponse,
      { regulationId: string }
    >({
      query: ({ regulationId }) => ({
        url: `regulations/${regulationId}/highlights`,
        method: "GET",
      }),
      transformResponse: (response: { data: any[] }) => {
        // Backend returns camelCase, but IHighlight uses snake_case
        return {
          data: response.data.map(
            (highlight): IHighlight => ({
              id: highlight.id,
              group_id: highlight.groupId, // Map camelCase to snake_case
              position: {
                pageNumber: highlight.pageNumber,
                boundingRect: highlight.boundingRect,
                rects: highlight.rects,
              },
              content: highlight.content || { text: "" },
              comment: highlight.comment || { text: "", emoji: "" },
            })
          ),
        };
      },
    }),
    evaluateQuery: builder.mutation<
      RunEvaluationResponse,
      RunEvaluationRequest
    >({
      query: (body: RunEvaluationRequest) => {
        return {
          url: `/evaluate`,
          method: "POST",
          body,
        };
      },
    }),
    getEvaluationExamples: builder.query<
      GetEvaluationExamplesResponse,
      { pane_id: string }
    >({
      query: ({ pane_id }) => ({
        url: `/panes/${pane_id}/evaluation-examples`,
        method: "GET",
      }),
    }),
    /**
     * Update one tree within a processed clause (Uncheck only).
     * treeKind: "trigger" | "obligations" | "definition"
     * Returns the updated processed clause (same shape as one item from getProcessedClauses).
     */
    updateProcessedClauseTree: builder.mutation<
      { data: Record<string, unknown> },
      {
        regulationId: string;
        processedClauseId: string;
        paneId: string;
        treeKind: "trigger" | "obligations" | "definition";
        rootNode: ConditionNode;
      }
    >({
      query: ({
        regulationId,
        processedClauseId,
        paneId,
        treeKind,
        rootNode,
      }) => ({
        url: `regulations/${regulationId}/processed-clauses/${processedClauseId}/tree`,
        method: "PATCH",
        body: { pane_id: paneId, tree_kind: treeKind, root_node: rootNode },
      }),
      invalidatesTags: [{ type: "ProcessedClause", id: "LIST" }],
    }),
  }),
});

export const {
  useGetProcessedClausesQuery,
  useGetHighlightsQuery,
  useEvaluateQueryMutation,
  useGetEvaluationExamplesQuery,
  useReviewProcessedClauseMutation,
  useUpdateProcessedClauseTreeMutation,
  usePrefetch: usePrefetchProcessedClauseApi,
} = ProcessedClauseApi;

export const GuardrailEditDataApi = ingestionApi.injectEndpoints({
  endpoints: (builder) => ({
    getGuardrailEditDataByDocument: builder.query<
      GuardrailEditDataByDocumentResponse,
      { documentName: string }
    >({
      query: ({ documentName }) => ({
        url: `regulations/${documentName}/guardrail-edit-data`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetGuardrailEditDataByDocumentQuery,
  usePrefetch: usePrefetchGuardrailEditData,
} = GuardrailEditDataApi;
